import { DAY } from '@/sim/clock';
import type { EventBus } from '@/sim/events';
import type { LandWriter } from './band';
import type { PathRecord } from './types';

/**
 * Paths emerge; they are never placed (spec §22).
 *
 * Every completed walk is filed under its rounded endpoint pair (~128 m
 * cells, order-normalised, so there and back count together). The third
 * traversal of the same pair promotes the actual walked polyline — sampled
 * every ~64 m by the walker — to a PathRecord, emits path_formed, and stamps
 * a thin line of lost biomass along it so the wear shows even before a
 * dedicated renderer. Reuse strengthens (cap 1) and refreshes lastUsedAt;
 * disuse decays ~1/180 per day, and below 0.05 the path dissolves — the ONE
 * deletion the world allows itself, because a faded trail is genuinely gone.
 *
 * Walking ON a worn way is 1.25× faster — since 2026-08-23 the walker
 * genuinely routes along the polyline (`waypointsFor`) and the bonus belongs
 * to feet actually on it; the old end-to-end `speedMultiplier` rule is
 * retired with its last caller. The path pays for itself, which is why it
 * keeps existing.
 *
 * Determinism: recordTravel fires only from BandSystem's hourly arrivals and
 * decay only from its daily boundary; ids mint from creation order. No
 * clocks, no dice.
 */

/** Walkers sample their trace roughly this often, in metres. */
export const PATH_SAMPLE = 64;
/** Speed multiplier for travel between a path's two ends. */
export const PATH_SPEED = 1.25;

/**
 * Endpoint rounding: walks are "the same trip" at this granularity. Coarse on
 * purpose — a camp is an area, not a point, and hunts leave from wherever the
 * morning found the band. At 128 m the soak wore no path in two years; the
 * trips were the same in every human sense but never in key.
 */
const PAIR_CELL = 384;
/** Traversals of a pair before the trail becomes a path. */
const FORM_COUNT = 3;
/** Strength a newly formed path opens with. */
const FORM_STRENGTH = 0.35;
/** Strength each reuse adds, up to the cap of 1. */
const REUSE_GAIN = 0.15;
/** Strength lost per unused day (~half a year to silence). */
const DECAY_PER_DAY = 1 / 180;
/** Below this the trail has genuinely faded back into the land. */
const DISSOLVE = 0.05;
/** Walks shorter than this round to the same ground; no trip to remember. */
const MIN_SPAN = 192;
/** "Near an end" for the speed bonus, in metres. */
const END_NEAR = 150;
/**
 * Routing and merging (2026-08-23, Griffin: mortals should USE a path
 * instead of creating a new one parallel to the original). Measured first
 * (`tools/parallels.ts` on the third century): 40 ways, 49.7 km — and 25
 * directed pairs ran ≥60% of their length within 48 m of another way,
 * `path-57` 100% within 48 m of `path-58`. A quarter of the network was the
 * same way twice, because every walk was a straight line filed under a
 * 384 m endpoint pair: near-miss endpoints wore near-parallel lines, and
 * nothing ever walked ALONG what was already worn.
 */
/** A trace this share of whose points run within MERGE_NEAR of an existing
 *  way IS that way — strengthen it, never lay a twin beside it. */
const MERGE_NEAR = 48;
const MERGE_SHARE = 0.6;
/**
 * How much longer, in TIME, the routed trip may take and still be taken —
 * the currency a walker actually spends. On-path metres cost 1/PATH_SPEED,
 * so at 1.15 the acceptable DISTANCE detour reaches ~1.44× when the whole
 * run is on the way: a right-angle way against the diagonal (1.41×, the
 * worst honest case) is walked, and anything worse is refused. A plain
 * distance bound rejected exactly the L-shaped ways mortals wear most.
 */
const TIME_DETOUR = 1.15;
/** Within this of the polyline counts as walking ON the way (speed, wear). */
const ON_PATH = 48;
/** The most polyline vertices handed back per hour of routed walking. */
const ROUTE_AHEAD = 8;

/** Distance from a point to a polyline, segment-projected. */
const toPolyline = (
  x: number, z: number, pts: ReadonlyArray<{ x: number; z: number }>,
): number => {
  let best = Infinity;
  for (let i = 0; i + 1 < pts.length; i++) {
    const ax = pts[i].x, az = pts[i].z;
    const dx = pts[i + 1].x - ax, dz = pts[i + 1].z - az;
    const L2 = dx * dx + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / L2)) : 0;
    best = Math.min(best, Math.hypot(x - (ax + t * dx), z - (az + t * dz)));
  }
  return best;
};
/**
 * The stamped wear per sampled point. The intent is a thin ~8 m line
 * (spec §22), but the EnvField lattice is 32 m — one cell is the thinnest
 * mark the land can hold, so the radius covers one cell and the biomass
 * carries the thinness.
 */
const WEAR_RADIUS = 36;
const WEAR_BIOMASS = -0.25;

const pairKey = (a: { x: number; z: number }, b: { x: number; z: number }): string => {
  const ka = `${Math.round(a.x / PAIR_CELL)},${Math.round(a.z / PAIR_CELL)}`;
  const kb = `${Math.round(b.x / PAIR_CELL)},${Math.round(b.z / PAIR_CELL)}`;
  return ka <= kb ? `${ka}|${kb}` : `${kb}|${ka}`;
};

export class PathSystem {
  private readonly list: PathRecord[] = [];
  private readonly pairs = new Map<string, { count: number; pathId?: string }>();
  private versionCounter = 1;
  private nextId = 0;

  constructor(private readonly events: EventBus) {}

  paths(): readonly PathRecord[] {
    return this.list;
  }

  /** Bumps whenever the path set changes; renderers rebuild geometry on change. */
  get version(): number {
    return this.versionCounter;
  }

  /**
   * A walk finished. Either it strengthens the path it retraced, or it counts
   * toward one — and on the third count, the trail becomes a path. Returns
   * the chunk keys whose land the new path visibly wore.
   */
  recordTravel(
    points: ReadonlyArray<{ x: number; z: number }>,
    t: number,
    land: LandWriter,
  ): string[] {
    if (points.length < 2) return [];
    const a = points[0], b = points[points.length - 1];
    if (Math.hypot(b.x - a.x, b.z - a.z) < MIN_SPAN) return [];
    const key = pairKey(a, b);
    const entry = this.pairs.get(key) ?? { count: 0 };
    this.pairs.set(key, entry);

    if (entry.pathId !== undefined) {
      const path = this.list.find((p) => p.id === entry.pathId);
      if (path) {
        // FEET OVER BOOKKEEPING (2026-08-23). Routing sends this pair's
        // walkers along whichever way serves best — which may be the
        // remembered twin's rival. Credit the way the walk physically ran,
        // and re-point the pair to it, so the unwalked twin stops being
        // refreshed and decay can finally have it. Without this the +5y
        // measurement kept 8 directed duplicate pairs of 25 alive for ever:
        // each twin fed on its own pair's credit while the walkers shared
        // one roadbed.
        const walked = this.alongExisting(points);
        const credit = walked ?? path;
        credit.strength = Math.min(1, credit.strength + REUSE_GAIN);
        credit.lastUsedAt = t;
        if (credit !== path) entry.pathId = credit.id;
        return [];
      }
      // the remembered path dissolved; the trail starts earning again
      delete entry.pathId;
      entry.count = 0;
    }

    entry.count++;
    if (entry.count < FORM_COUNT) return [];

    // the walk ran along an existing way for most of its length: that way IS
    // the path. Strengthen it and remember the pair as its — never lay a twin
    // beside it.
    const along = this.alongExisting(points);
    if (along) {
      along.strength = Math.min(1, along.strength + REUSE_GAIN);
      along.lastUsedAt = t;
      entry.pathId = along.id;
      return [];
    }

    const rec: PathRecord = {
      id: `path-${this.nextId++}`,
      points: points.map((p) => ({ x: p.x, z: p.z })),
      strength: FORM_STRENGTH,
      createdAt: t,
      lastUsedAt: t,
    };
    this.list.push(rec);
    entry.pathId = rec.id;
    this.versionCounter++;

    const mx = (a.x + b.x) / 2, mz = (a.z + b.z) / 2;
    this.events.emit({
      type: 'path_formed', time: t, x: mx, z: mz,
      radius: Math.hypot(b.x - a.x, b.z - a.z) / 2,
      magnitude: rec.points.length, salience: 0.4,
      cause: { kind: 'mortal' },
    });

    // the wear that made the path visible: a thin browned line, cell by cell
    const dirty = new Set<string>();
    for (const p of rec.points) {
      for (const k of land.stamp(p.x, p.z, WEAR_RADIUS, { biomass: WEAR_BIOMASS }, t)) {
        dirty.add(k);
      }
    }
    return [...dirty];
  }

  /**
   * One absolute day boundary: unused paths fade, and a path faded below
   * threshold dissolves — genuinely deleted, trail and count alike.
   */
  daily(t: number): void {
    for (let i = this.list.length - 1; i >= 0; i--) {
      const p = this.list[i];
      if (t - p.lastUsedAt <= DAY) continue;
      p.strength -= DECAY_PER_DAY;
      if (p.strength >= DISSOLVE) continue;
      this.list.splice(i, 1);
      this.versionCounter++;
      for (const [key, entry] of this.pairs) {
        if (entry.pathId === p.id) { this.pairs.delete(key); break; }
      }
    }
  }

  /**
   * The existing way this walk physically ran along, if any: the first (in
   * creation order, which is deterministic) whose polyline holds ≥60% of the
   * walked points within 48 m. Null when the walk broke its own ground.
   */
  private alongExisting(
    points: ReadonlyArray<{ x: number; z: number }>,
  ): PathRecord | undefined {
    for (const p of this.list) {
      let near = 0;
      for (const q of points) if (toPolyline(q.x, q.z, p.points) <= MERGE_NEAR) near++;
      if (near / points.length >= MERGE_SHARE) return p;
    }
    return undefined;
  }

  /**
   * The way to walk from here to there, if a worn way serves the trip: the
   * next few polyline vertices to follow, and whether the walker is already
   * ON the way (within 48 m — the speed bonus and the wear both belong to
   * feet actually on it). Null when no way serves: joins past 150 m, runs
   * of fewer than two vertices, or a total (join + along + leave) beyond
   * 1.35× the direct line are all refusals — a path pays for itself or it
   * is not a route, it is scenery.
   *
   * Stateless and recomputed each hour from (position, target): no stored
   * route, nothing to save, and a restored world walks the identical bend
   * (law 7). As the walker advances, its nearest vertex advances with it, so
   * the same call naturally plays the polyline through and hands over to
   * the direct line at the leave point.
   */
  waypointsFor(
    pos: { x: number; z: number }, target: { x: number; z: number },
  ): { points: Array<{ x: number; z: number }>; onPath: boolean } | null {
    const direct = Math.hypot(target.x - pos.x, target.z - pos.z);
    if (direct < MIN_SPAN) return null;
    let best: { path: PathRecord; i: number; j: number; score: number } | null = null;
    for (const p of this.list) {
      const pts = p.points;
      if (pts.length < 3) continue;
      let i = 0, di = Infinity, j = 0, dj = Infinity;
      for (let k = 0; k < pts.length; k++) {
        const dp = Math.hypot(pts[k].x - pos.x, pts[k].z - pos.z);
        if (dp < di) { di = dp; i = k; }
        const dt = Math.hypot(pts[k].x - target.x, pts[k].z - target.z);
        if (dt < dj) { dj = dt; j = k; }
      }
      if (di > END_NEAR || dj > END_NEAR || Math.abs(i - j) < 2) continue;
      let along = 0;
      const lo = Math.min(i, j), hi = Math.max(i, j);
      for (let k = lo; k < hi; k++) {
        along += Math.hypot(pts[k + 1].x - pts[k].x, pts[k + 1].z - pts[k].z);
      }
      const total = di + along + dj;
      // priced in time: joins at walking pace, the run at path pace
      if (di + dj + along / PATH_SPEED > direct * TIME_DETOUR) continue;
      const score = along / total;
      if (!best || score > best.score ||
          (score === best.score && p.id < best.path.id)) {
        best = { path: p, i, j, score };
      }
    }
    if (!best) return null;
    const pts = best.path.points;
    const dir = best.j > best.i ? 1 : -1;
    // never walk BACK to a vertex already passed: if the walker projects past
    // its nearest vertex toward the next, start one further on
    let k = best.i;
    const nx = pts[k + dir];
    if (nx) {
      const vx = nx.x - pts[k].x, vz = nx.z - pts[k].z;
      if ((pos.x - pts[k].x) * vx + (pos.z - pts[k].z) * vz > 0) k += dir;
    }
    const points: Array<{ x: number; z: number }> = [];
    for (let n = 0; n < ROUTE_AHEAD; n++) {
      const idx = k + n * dir;
      if (dir > 0 ? idx > best.j : idx < best.j) break;
      points.push({ x: pts[idx].x, z: pts[idx].z });
    }
    if (points.length === 0) return null;
    return { points, onPath: toPolyline(pos.x, pos.z, pts) <= ON_PATH };
  }

}
